import { FormCommonApiService } from '../../../../../service/api/formcommon-api.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { ProductosFitosanitariosFormDefinition } from './form-control/form-definition';
import { Utils } from '../../../../../common/utils';
import moment from 'moment';

import { ProductosModel } from '../../../../../models/productos/productos.model';
import { ColInterface } from '../../../../../common/components/common-list-table/col.interface';
import { ColStylesInterface } from '../../../../../common/components/common-list-table/col-styles.interface';
import { TreeNode } from 'primeng/api';
import { baseRequest, baseSentence } from '../../../../../common/classes/request-builder';
import { OtrasDenominacionesApiService } from '../../../../../service/api/otrasdenominaciones-api.service';
import { ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import { ProductCatalogCacheService } from 'src/app/service/cache/product-catalog-cache.service';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { DropdownData, ReactiveFormStableValueChange, FormField, FormType } from '@fitogest/reactive-form-builder/interfaces';
import { getDropdownSelectedSingle, setDropdownSelected } from '@fitogest/reactive-form-builder/utils';
import { RequestHandler } from 'src/app/service/OffService/request-handler';

const PRODUCT_FORM_CACHE_TTL_MS = 300000;

@Component({
	selector: 'app-stock-formulario-productos',
	standalone: false,
	templateUrl: './productos-form.component.html',
	styleUrls: ['./productos-form.component.scss'],
})

export class ProductosFormComponent extends BaseForm<ProductosModel> implements OnInit, OnDestroy {
	public form: ProductosFitosanitariosFormDefinition = new ProductosFitosanitariosFormDefinition(
		this.getType(),
		this.getModel() as Record<string, string>,
		null,
	);

	public formRequest = new ReactiveFormRequestCoordinator<ProductosModel>();

	public vademecumTableRequest = new RequestHandler<ProductosModel[]>();

	public fecha_caducidad = '';

	public searchValue = {
		term: '',
	};

	private _dropdownsJustLoaded = false;

	private skipNextDenominationSourceSync = false;

    private vademecumSearchTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		public dashboardEvents: DashboardService,
		public formApi: FormCommonApiService,
		public productosApi: ProductosApiService,
		public otrasDenominaciones: OtrasDenominacionesApiService,
		public override router: Router,
		public override route: ActivatedRoute,
		private readonly productCatalogCache: ProductCatalogCacheService,
	) {
		super(
			route,
			router,
			dashboardEvents,
			productosApi.productos.PUT,
			productosApi.productos.POST,
			'productos',
			'Actualizar Fitosanitario',
			'Alta de Producto Fitosanitario',
		);
		this.formRequest = new ReactiveFormRequestCoordinator<ProductosModel>();
	}

	ngOnInit(): void {
		this.resetExternalDropdowns();

		this.productosApi.productos.POST.reset();
		this.productosApi.productos.PUT.reset();

		this.initFormData();
		this.softInit(this.getType());
	}

	public stableValueChanges(event: ReactiveFormStableValueChange<ProductosModel>): void {
		this.form.model = {
			...this.form.model,
			...event.value,
		};

		if (event.changedFields.includes('num_registro')) {
			this.handleRegisterNumberChange(event.value.num_registro);
		}

		if (event.changedFields.includes('nombre')) {
			this.setVademecumSearchValue(this.getVademecumSearchTerm());
		}

		if (event.changedFields.includes('otra_denominacion_comun')) {
			if (this.skipNextDenominationSourceSync) {
				this.skipNextDenominationSourceSync = false;
			} else {
				this.syncProductDropdownSource(true);
			}
		}
	}

	public productSelected(row: any): void {
		const product = (row?.data ?? row) as ProductosModel;

		if (!product) {
			return;
		}

		const patch = this.buildProductPatch(product);
		const productRegisterValue = this.getProductRegisterModelValue(product);

		setDropdownSelected(this.form.lists.productos, product);
		this.form.model = {
			...this.form.model,
			num_registro: productRegisterValue,
			...patch,
		};
		this.fecha_caducidad = product.fecha_caducidad ?? '';

		this.formBuilderRef.updateForm({
			num_registro: product,
			...patch,
		}, true);
	}

	public globalConditionalStyle(
		_cellValue: string,
		_colDefinition: ColInterface,
		_colGroup: ColInterface[],
		rowValue: TreeNode<ProductosModel>,
	): ColStylesInterface {
		const baseStyle: any = {
			color: 'black',
			backgroundColor: '#ffffff',
			borderBottom: '1px solid #dddddd',
		};

		const fechaCaducidad = Utils.toDate(rowValue.data?.fecha_caducidad ?? '');
		const fechaInscripcion = Utils.toDate(rowValue.data?.fecha_inscripcion ?? '');

		const diffCaducidad = moment().diff(fechaCaducidad, 'months');
		const diffInscripcion = moment().diff(fechaInscripcion, 'months');

		if (diffInscripcion < 12) {
			baseStyle.backgroundColor = '#cbdecb';
		}

		if (diffCaducidad > 12) {
			baseStyle.backgroundColor = '#222222';
			baseStyle.color = 'white';
		} else if (diffCaducidad > 6) {
			baseStyle.backgroundColor = '#ff7f7f';
		} else if (diffCaducidad >= 0) {
			baseStyle.backgroundColor = '#ffcf78';
		}

		return { rowStyle: baseStyle };
	}

	public globalVisualTransform(value: string): string {
		return value;
	}

    public override ngOnDestroy(): void {
        if (this.vademecumSearchTimer !== null) {
            clearTimeout(this.vademecumSearchTimer);
        }

        Utils.unsuscribe([
            this.formApi.magramaDetalleProducto,
        ]);

        Utils.unsuscribe([
            this.formApi.magramaProducto
        ]);
    }

	private initFormData(): Promise<boolean> {
		return Promise.all([
			this.productCatalogCache.getOrLoad<ProductosModel[]>(
				this.getProductCacheConfig('productos-vademecum'),
				() => baseRequest(this.formApi.productosVademecum) as Promise<ProductosModel[]>,
			),
			this.productCatalogCache.getOrLoad<ProductosModel[]>(
				this.getProductCacheConfig('otras-denominaciones'),
				() => baseRequest(this.otrasDenominaciones.otrasDenominacionesGET) as Promise<ProductosModel[]>,
			),
		])
			.then(([standardProducts, otherDenominations]) => {
				this.vademecumTableRequest.emitResponse(standardProducts);

				this.assignDropdownData(
					this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>,
					standardProducts,
					(item: ProductosModel) => {
						const level = Utils.checkIfProductIsValid(item.fecha_caducidad, 'fitosanitario');

						return `${level} [${item.referencia}] ${item.nombre_comercial}`;
					},
				);

				this.assignDropdownData(
					this.form.lists.denominaciones_otras as DropdownData<ProductosModel>,
					otherDenominations,
					(item: ProductosModel) => `[${item.num_registro}] ${item.denominaciones_comunes} - ${item.nombre_comercial}`,
				);

				const useOtherCommonDenomination = this.isOtherCommonDenominationEnabled(this.getModel().otra_denominacion_comun);
				const dropdownData = useOtherCommonDenomination
					? this.form.lists.denominaciones_otras as DropdownData<ProductosModel>
					: this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>;

				this.form.lists.productos = dropdownData;
				this.form.model.otra_denominacion_comun = useOtherCommonDenomination ? '1' : '0';
				this.updateRegisterNumberFieldDropdown(dropdownData, useOtherCommonDenomination);

				this.finalizeExternalDropdowns();

				if ([FormType.EDITION, FormType.DUPLICATION].includes(this.getType())) {
					void this.restoreSelectedProductForEditionWhenReady();
				}

				return true;
			})
			.catch((error: unknown) => {
				console.error('Error loading product form dropdowns:', error);
				this.finalizeExternalDropdowns();

				return false;
			});
	}

	private assignDropdownData<T>(target: DropdownData<T>, source: T[], label: (item: T) => string): void {
		const options = baseSentence(source, label, true);

		target.filtered = [...options];
		target.values = [...options];
	}

	private async restoreSelectedProductForEditionWhenReady(): Promise<void> {
		await this.formBuilderRef.whenFieldsReady(['otra_denominacion_comun', 'num_registro']);
		this.restoreSelectedProductForEdition();
	}

	private restoreSelectedProductForEdition(): void {
		const selection = this.resolveSelectedProductForEdition();
		const dropdownData = selection.useOtherCommonDenomination
			? this.form.lists.denominaciones_otras as DropdownData<ProductosModel>
			: this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>;
		const otherCommonDenominationControlValue = selection.useOtherCommonDenomination ? 1 : 0;
		const otherCommonDenominationModelValue = selection.useOtherCommonDenomination ? '1' : '0';

		this.form.lists.productos = dropdownData;
		this.setOtherCommonDenominationModelValue(selection.useOtherCommonDenomination);
		this.updateRegisterNumberFieldDropdown(dropdownData, selection.useOtherCommonDenomination);
		setDropdownSelected(dropdownData, selection.product);

		if (selection.product === null) {
			this.form.model = {
				...this.form.model,
				num_registro: null,
			};
			this._dropdownsJustLoaded = true;
			this.skipNextDenominationSourceSync = true;
			this.formBuilderRef.updateForm({
				otra_denominacion_comun: otherCommonDenominationControlValue,
				num_registro: null,
			}, false);
			this.patchOtherCommonDenominationControl(selection.useOtherCommonDenomination);

			return;
		}

		const patch = this.buildProductPatch(selection.product);
		const productRegisterValue = this.getProductRegisterModelValue(selection.product);

		this.form.model = {
			...this.form.model,
			otra_denominacion_comun: otherCommonDenominationModelValue,
			num_registro: productRegisterValue,
			...patch,
		};
		this.fecha_caducidad = selection.product.fecha_caducidad ?? '';
		this._dropdownsJustLoaded = true;
		this.skipNextDenominationSourceSync = true;

		this.formBuilderRef.updateForm({
			otra_denominacion_comun: otherCommonDenominationControlValue,
			num_registro: selection.product,
			...patch,
		}, false);
		this.patchOtherCommonDenominationControl(selection.useOtherCommonDenomination);
	}

	private setOtherCommonDenominationModelValue(useOtherCommonDenomination: boolean): void {
		this.form.model = {
			...this.form.model,
			otra_denominacion_comun: useOtherCommonDenomination ? '1' : '0',
		};
	}

	private patchOtherCommonDenominationControl(useOtherCommonDenomination: boolean): void {
		this.setOtherCommonDenominationModelValue(useOtherCommonDenomination);
		this.formBuilderRef.updateForm({
			otra_denominacion_comun: useOtherCommonDenomination ? 1 : 0,
		}, false);
	}

	private resolveSelectedProductForEdition(): { product: ProductosModel | null; useOtherCommonDenomination: boolean } {
		const model = this.getModel();
		const explicitOtherCommonDenomination = this.isOtherCommonDenominationEnabled(model.otra_denominacion_comun);
		const otherProduct = this.findOtherCommonDenominationProduct(model, false);

		if (explicitOtherCommonDenomination) {
			return {
				product: otherProduct,
				useOtherCommonDenomination: true,
			};
		}

		const inferredOtherProduct = this.findOtherCommonDenominationProduct(model, true);

		if (inferredOtherProduct !== null) {
			return {
				product: inferredOtherProduct,
				useOtherCommonDenomination: true,
			};
		}

		return {
			product: this.findStandardProduct(model),
			useOtherCommonDenomination: false,
		};
	}

	private findStandardProduct(model: ProductosModel): ProductosModel | null {
		return (this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>).values
			.map((option) => option.raw ?? option.value)
			.find((product): product is ProductosModel => {
				if (!this.isProductModel(product)) {
					return false;
				}

				return this.hasSameProductIdentifier(product.referencia, model.num_registro);
			}) ?? null;
	}

	private findOtherCommonDenominationProduct(model: ProductosModel, requireNameMatch: boolean): ProductosModel | null {
		return (this.form.lists.denominaciones_otras as DropdownData<ProductosModel>).values
			.map((option) => option.raw ?? option.value)
			.find((product): product is ProductosModel => {
				if (!this.isProductModel(product)) {
					return false;
				}

				if (!this.hasSameProductIdentifier(product.num_registro, model.num_registro)) {
					return false;
				}

				return !requireNameMatch || this.matchesEditionProductName(product, model);
			}) ?? null;
	}

	private isProductModel(value: unknown): value is ProductosModel {
		return typeof value === 'object' && value !== null && !Array.isArray(value);
	}

	private matchesEditionProductName(product: ProductosModel, model: ProductosModel): boolean {
		const modelName = this.normalizeComparableString(model.nombre);

		if (modelName.length === 0) {
			return false;
		}

		return [
			product.denominaciones_comunes,
			product.nombre_comercial,
			product.nombre,
		]
			.map((value) => this.normalizeComparableString(value))
			.includes(modelName);
	}

	private hasSameProductIdentifier(left: unknown, right: unknown): boolean {
		const normalizedLeft = this.normalizeComparableString(left);
		const normalizedRight = this.normalizeComparableString(right);

		return normalizedLeft.length > 0 && normalizedLeft === normalizedRight;
	}

	private normalizeComparableString(value: unknown): string {
		return String(value ?? '').trim().toLowerCase();
	}

 
private getVademecumSearchTerm(): string {
    if (!this.isOtherCommonDenominationEnabled()) {
        return this.form.model.nombre ?? '';
    }

    const selected = getDropdownSelectedSingle(this.form.lists.denominaciones_otras as DropdownData<ProductosModel>);

    if (selected === null || Array.isArray(selected)) {
        return this.form.model.nombre ?? '';
    }

    return selected.nombre_comercial ?? this.form.model.nombre ?? '';
}


private handleRegisterNumberChange(value: unknown): void {
    if (this._dropdownsJustLoaded) {
        this._dropdownsJustLoaded = false;
        return;
    }

    const selectedProduct = this.getSelectedProduct(value);

    if (selectedProduct === null) {
        this.setVademecumSearchValue('');
        return;
    }

    const patch = this.buildProductPatch(selectedProduct);

    setDropdownSelected(this.form.lists.productos, selectedProduct);

    this.form.model = {
        ...this.form.model,
        ...patch,
    };
    this.fecha_caducidad = selectedProduct.fecha_caducidad ?? '';

    this.formBuilderRef.updateForm(patch, false);
    this.setVademecumSearchValue(this.getSelectedProductSearchTerm(selectedProduct));
}

private getSelectedProduct(value: unknown): ProductosModel | null {
    const resolved = this.formBuilderRef?.resolveDropdownRawValue<ProductosModel>('num_registro', value);

    if (resolved !== null && resolved !== undefined) {
        return resolved;
    }

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        return value as ProductosModel;
    }

    return this.getActiveProductDropdown().values
        .map((option) => option.raw ?? option.value)
        .find((product): product is ProductosModel => this.matchesProductIdentifier(product, value)) ?? null;
}

private buildProductPatch(product: ProductosModel): Pick<ProductosModel, 'nombre' | 'materia_activa'> {
    const productName = this.isOtherCommonDenominationEnabled()
        ? this.firstNonEmptyString(product.denominaciones_comunes, product.nombre_comercial, product.nombre)
        : this.firstNonEmptyString(product.nombre_comercial, product.nombre);

    return {
        nombre: productName,
        materia_activa: this.getProductActiveMatter(product),
    };
}

private getProductActiveMatter(product: ProductosModel): string {
    return this.firstNonEmptyString(
        product.composicion,
        product.materia_activa,
        product.producto?.composicion,
        product.producto?.materia_activa,
    );
}


private getProductRegisterModelValue(product: ProductosModel): string | number {
    const reference = this.toScalarProductIdentifier(product.referencia);

    if (reference !== '') {
        return reference;
    }

    return this.toScalarProductIdentifier(product.num_registro);
}

private toScalarProductIdentifier(value: unknown): string | number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        return value.trim();
    }

    return '';
}

private firstNonEmptyString(...values: unknown[]): string {
    return values
        .map((value) => String(value ?? '').trim())
        .find((value) => value.length > 0 && value !== '-') ?? '';
}

private getActiveProductDropdown(): DropdownData<ProductosModel> {
    return this.isOtherCommonDenominationEnabled()
        ? this.form.lists.denominaciones_otras as DropdownData<ProductosModel>
        : this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>;
}

private matchesProductIdentifier(value: unknown, selectedValue: unknown): value is ProductosModel {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }

    const product = value as ProductosModel;
    const selected = String(selectedValue ?? '').trim();

    if (selected.length === 0) {
        return false;
    }

    return [product.referencia, product.num_registro]
        .map((identifier) => String(identifier ?? '').trim())
        .includes(selected);
}

private getSelectedProductSearchTerm(product: ProductosModel): string {
    const values = [
        product.referencia,
        product.num_registro,
        product.nombre_comercial,
        product.nombre,
    ];

    return values
        .map((item) => String(item ?? '').trim())
        .find((item) => item.length > 0) ?? '';
}


private setVademecumSearchValue(term: string): void {
    this.searchValue = {
        term: term.trim(),
    };
}

	private getProductCacheConfig(name: string): {
		namespace: string;
		name: string;
		ttlMs: number;
		userScope: string;
		clientScope: string;
	} {
		return {
			namespace: 'producto-add-form',
			name,
			ttlMs: PRODUCT_FORM_CACHE_TTL_MS,
			userScope: String(StorageManager.getUser()?.id ?? 'anonymous'),
			clientScope: String(StorageManager.getClient()?.id ?? 'no-client'),
		};
	}

    private syncProductDropdownSource(clearSelectedProduct: boolean): void {
    const useOtherCommonDenomination = this.isOtherCommonDenominationEnabled();

    const dropdownData = useOtherCommonDenomination
        ? this.form.lists.denominaciones_otras as DropdownData<ProductosModel>
        : this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>;

    this.form.lists.productos = dropdownData;
    this.updateRegisterNumberFieldDropdown(dropdownData, useOtherCommonDenomination);

    if (clearSelectedProduct) {
        this.clearSelectedProductAfterDenominationChange();
    }

    this.dropdownLoadTrigger.next();
}
private updateRegisterNumberFieldDropdown(
    dropdownData: DropdownData<ProductosModel>,
    useOtherCommonDenomination: boolean,
): void {
    const field = this.getRegisterNumberField();

    if (field === null || field.input.dropdownConfiguration === undefined) {
        return;
    }

    field.input.data = dropdownData;
    field.input.dropdownConfiguration.valuePrimaryKey = useOtherCommonDenomination
        ? 'num_registro'
        : 'referencia';
}

private getRegisterNumberField(): FormField<ProductosModel> | null {
    for (const section of this.form.formFields.sections ?? []) {
        const found = section.fields.find((field) => field.name === 'num_registro') as FormField<ProductosModel> | undefined;

        if (found !== undefined) {
            return found;
        }
    }

    return null;
}

private clearSelectedProductAfterDenominationChange(): void {
    setDropdownSelected(this.form.lists.productos, null);
    this.fecha_caducidad = '';
    this.searchValue = { term: '' };

    this.form.model = {
        ...this.form.model,
        num_registro: null,
        nombre: '',
        materia_activa: '',
    };

    this.formBuilderRef.updateForm({
        num_registro: null,
        nombre: '',
        materia_activa: '',
    }, false);
}

private isOtherCommonDenominationEnabled(value: unknown = this.form.model.otra_denominacion_comun): boolean {
    return value === '1' || value === 1 || value === true;
}


	override goBack(): void {
		window.history.back();
	}

    

	public get productoSeleccionado(): ProductosModel | null {
		return getDropdownSelectedSingle(this.form.lists.productos);
	}
}
